/**
 *
 * ModalExtraData
 *
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import _className from 'classnames';
import LoadingOverlay from 'components/LoadingOverlay';
import Hidden from '@material-ui/core/Hidden';
import Box from '@material-ui/core/Box';
import Divider from 'components/Divider';
import Grid from '@material-ui/core/Grid';
import { updateAccount } from 'containers/Account/actions';
import { makeSelectProcessing } from 'containers/Account/selectors';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import ProfileTextField from 'components/ProfileTextField';
import MonthPicker from 'components/MonthPicker';
import DemographicsSelect from 'components/DemographicsSelect';
import SubscriptionOptions from 'components/SubscriptionOptions';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import {
  makeSelectProfile,
  makeSelectSubscriptionStatus,
  makeSelectSubscriptionProcessing,
} from 'containers/Auth/selectors';
import { getSubscriptionStatus } from 'containers/Auth/actions';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import useSiteCopySelector from 'components/useSiteCopySelector';
import QuoteIcon from './images/quote-icon.svg';

const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: 'white',
    maxHeight: '90%',
    overflowY: 'auto',
    position: 'absolute',
    top: '5%',
    [theme.breakpoints.up('md')]: {
      left: '50%',
      width: 900,
      marginLeft: -450,
    },
    [theme.breakpoints.down('sm')]: {
      width: '90%',
      left: 0,
      right: 0,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  column: {
    padding: '16px 32px',
  },
  coloredColumn: {
    backgroundColor: '#F9FDFF',
  },
  header: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  headerText: {
    paddingTop: 6,
  },
  closeIcon: {
    position: 'absolute',
    right: 12,
    top: 12,
    padding: 6,
  },
  button: {
    background: theme.palette.text.secondary,
    color: '#ffffff',
    width: '100%',
    padding: 12,
    marginTop: 8,
    marginBottom: 5,
    textTransform: 'uppercase',
    '&:hover': {
      opacity: 0.8,
    },
    ...theme.typography.pSmallBold,
  },
  benefitWrapper: {
    display: 'flex',
    marginTop: 32,
    marginBottom: 32,
    alignItems: 'center',
  },
  benefitImage: {
    marginRight: 16,
    height: 49,
    width: 49,
  },
  benefitLabel: {
    fontFamily: 'PoppinsRegular',
    fontSize: 14,
    lineHeight: '19px',
  },
  feedbackWrapper: {
    position: 'relative',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E9ECED',
    borderRadius: 6,
    padding: '24px 24px 24px 56px',
    marginBottom: 16,
    '&::after': {
      top: '100%',
      left: '85%',
      border: 'solid transparent',
      content: '""',
      height: 0,
      width: 0,
      position: 'absolute',
      pointerEvents: 'none',
      borderColor: 'rgba(255, 255, 255, 0)',
      borderTopColor: '#FFFFFF',
      borderLeftWidth: 26,
      borderTopWidth: 13,
      borderRightWidth: 0,
    },
    '&::before': {
      top: '100%',
      left: '85%',
      border: 'solid transparent',
      content: '""',
      height: 0,
      width: 0,
      position: 'absolute',
      pointerEvents: 'none',
      borderColor: 'rgba(233, 236, 237, 0)',
      borderTopColor: '#E9ECED',
      borderLeftWidth: 27,
      borderTopWidth: 14,
      borderRightWidth: 0,
      marginTop: 1,
    },
  },
  quoteIcon: {
    position: 'absolute',
    top: 24,
    left: 24,
  },
  feedbackText: {
    fontFamily: 'PoppinsRegular',
    fontSize: 14,
    lineHeight: '21px',
  },
  feedbackUserWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  feedbackUserInfo: {
    '& > p:first-child': {
      fontFamily: 'PoppinsBold',
      fontSize: 14,
      lineHeight: '19px',
      textAlign: 'right',
    },
    '& > p:last-child': {
      fontFamily: 'PoppinsRegular',
      fontSize: 12,
      lineHeight: '19px',
      textAlign: 'right',
    },
  },
  feedbackUserImage: {
    height: 45,
    width: 45,
    borderRadius: '50%',
    marginLeft: 16,
    marginRight: 32,
    objectFit: 'cover',
  },
}));

const stateSelector = createStructuredSelector({
  accountProcessing: makeSelectProcessing(),
  clientDetails: makeSelectClientDetails(),
  profile: makeSelectProfile(),
  subscriptionProcessing: makeSelectSubscriptionProcessing(),
  subscriptionStatus: makeSelectSubscriptionStatus(),
});

const ModalExtraData = ({ onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    accountProcessing,
    clientDetails,
    profile,
    subscriptionProcessing,
    subscriptionStatus,
  } = useSelector(stateSelector);
  const [siteConfig] = useSiteConfigSelector(['Age and Gender Mappings']);
  const initialProfile = useRef(profile);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [monthYear, setMonthYear] = useState(null);
  const [gender, setGender] = useState('-1');
  const [genderOther, setGenderOther] = useState('');
  const [genderLGBTQp, setGenderLGBTQp] = useState('-1');

  const [accountSiteCopy, signUpSiteCopy] = useSiteCopySelector([
    'account',
    'sign-up',
  ]);

  const [subscription, setSubscription] = useState(
    _get(clientDetails, 'metadata.hideSubscription')
      ? []
      : _get(accountSiteCopy, 'pageCopy.subscription', [])
          .filter(item => item.default)
          .map(item => item.value),
  );

  useEffect(() => {
    if (!profile.isEmpty) {
      const shouldUsePersonalEmail = _get(
        clientDetails,
        'metadata.requirePersonalEmail',
        false,
      );
      const profileEmail = shouldUsePersonalEmail
        ? profile.personalEmail
        : profile.email;
      if (
        profileEmail &&
        !subscriptionProcessing &&
        subscriptionStatus.email !== profileEmail
      ) {
        dispatch(getSubscriptionStatus(profileEmail));
      }
    }
  }, [
    clientDetails,
    dispatch,
    profile,
    subscriptionProcessing,
    subscriptionStatus,
  ]);

  const handleFormChange = e => {
    if (e.target.name === 'firstName') setFirstName(e.target.value);
    else if (e.target.name === 'lastName') setLastName(e.target.value);
    else if (e.target.name === 'gender') {
      if (e.target.value !== 'Other') {
        setGenderOther('');
      }
      setGender(e.target.value);
    } else if (e.target.name === 'genderOther') setGenderOther(e.target.value);
    else if (e.target.name === 'genderLGBTQp') setGenderLGBTQp(e.target.value);
  };

  const handleMonthYearChange = value => setMonthYear(value);

  const handleSubscriptionChange = value => {
    if (subscription.includes(value)) {
      setSubscription(subscription.filter(item => item !== value));
    } else {
      setSubscription(subscription.concat(value));
    }
  };

  const handleSubmit = () => {
    const data = {};
    if (requireFirstName) data.firstName = firstName;
    if (requireLastName) data.lastName = lastName;
    if (requireMonthYear && monthYear) data.birthdayMonthYear = monthYear;
    if (requireGender && gender && gender !== '-1') data.gender = gender;
    if (requireGender && genderOther !== '') data.genderOther = genderOther;
    if (requireGender && genderLGBTQp !== '-1')
      data.genderLGBTQp = genderLGBTQp;

    if (!_isEmpty(data) || !_isEmpty(subscription))
      dispatch(
        updateAccount({
          data,
          subscription,
          signUpCompleted: true,
        }),
      );
  };

  const requireFirstName = !_get(initialProfile.current, 'firstName');
  const requireLastName = !_get(initialProfile.current, 'lastName');
  const requireMonthYear = !_get(initialProfile.current, 'birthdayMonthYear');
  const requireGender = !_get(initialProfile.current, 'gender');
  const requireSubscription = !_get(subscriptionStatus, 'status');

  return (
    <>
      <Grid container justifyContent="center" className={classes.container}>
        <IconButton
          className={classes.closeIcon}
          onClick={onClose}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
        <Grid item xs={12} md={7} className={classes.column}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="h5"
                component="span"
                color="primary"
                className={classes.headerText}
              >
                {_get(signUpSiteCopy, 'pageCopy.completeRegistrationHeader')}
              </Typography>
              {_get(
                signUpSiteCopy,
                'pageCopy.completeRegistrationCallToAction',
              ) && (
                <Typography variant="body2" color="textPrimary" gutterBottom>
                  {_get(
                    signUpSiteCopy,
                    'pageCopy.completeRegistrationCallToAction',
                  )}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {requireFirstName && (
                  <Grid item xs={6}>
                    <ProfileTextField
                      field="firstName"
                      value={firstName}
                      onChange={handleFormChange}
                      plain
                      required
                    />
                  </Grid>
                )}
                {requireLastName && (
                  <Grid item xs={6}>
                    <ProfileTextField
                      field="lastName"
                      value={lastName}
                      onChange={handleFormChange}
                      plain
                      required
                    />
                  </Grid>
                )}
                {requireMonthYear && (
                  <Grid item xs={6}>
                    <MonthPicker
                      value={monthYear}
                      onChange={handleMonthYearChange}
                      disableFutureDates
                      plain
                    />
                  </Grid>
                )}
                {requireGender && (
                  <>
                    <Grid item xs={6}>
                      <DemographicsSelect
                        type="gender"
                        onChange={handleFormChange}
                        value={gender}
                        plain
                      />
                    </Grid>
                    {gender === 'Other' && (
                      <Grid item xs={12} sm={6}>
                        <ProfileTextField
                          field="genderOther"
                          value={genderOther}
                          onChange={handleFormChange}
                          plain
                        />
                      </Grid>
                    )}
                    {!_get(
                      siteConfig,
                      'config.genderLGBTQp.excludeClients',
                      [],
                    ).includes(clientDetails.shortName) && (
                      <Grid item xs={12} sm={6}>
                        <DemographicsSelect
                          type="genderLGBTQp"
                          onChange={handleFormChange}
                          value={genderLGBTQp}
                          plain
                        />
                      </Grid>
                    )}
                  </>
                )}
                {!_get(clientDetails, 'metadata.hideSubscription') &&
                  requireSubscription && (
                    <Grid item xs={12}>
                      <SubscriptionOptions
                        subscription={subscription}
                        onChange={handleSubscriptionChange}
                        isSmall
                      />
                    </Grid>
                  )}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <ButtonBase className={classes.button} onClick={handleSubmit}>
                {_get(signUpSiteCopy, 'pageCopy.completeRegistrationButton')}
              </ButtonBase>
            </Grid>
          </Grid>
        </Grid>
        <Hidden smDown>
          <Grid
            item
            md={5}
            className={_className(classes.column, classes.coloredColumn)}
          >
            <Typography
              variant="h5"
              color="textPrimary"
              gutterBottom
              className={classes.headerText}
            >
              {_get(signUpSiteCopy, 'pageCopy.benefitsHeader')}
            </Typography>
            {_get(signUpSiteCopy, 'pageCopy.benefitsItems', []).map(item => (
              <Box component="div" className={classes.benefitWrapper}>
                <img
                  src={item.image}
                  className={classes.benefitImage}
                  alt={item.label}
                />
                <Typography
                  color="primaryText"
                  className={classes.benefitLabel}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
            <Divider top={32} bottom={24} />
            <Box component="div" className={classes.feedbackWrapper}>
              <img src={QuoteIcon} className={classes.quoteIcon} alt="Quote" />
              <Typography color="primaryText" className={classes.feedbackText}>
                {_get(signUpSiteCopy, 'pageCopy.feedbackText')}
              </Typography>
            </Box>
            <Box component="div" className={classes.feedbackUserWrapper}>
              <Box component="div" className={classes.feedbackUserInfo}>
                <Typography color="textPrimary">
                  {_get(signUpSiteCopy, 'pageCopy.feedbackName')}
                </Typography>
                <Typography color="textPrimary">
                  {_get(signUpSiteCopy, 'pageCopy.feedbackSubtitle')}
                </Typography>
              </Box>
              <img
                src={_get(signUpSiteCopy, 'pageCopy.feedbackImage')}
                className={classes.feedbackUserImage}
                alt={_get(signUpSiteCopy, 'pageCopy.feedbackName')}
              />
            </Box>
          </Grid>
        </Hidden>
      </Grid>
      <LoadingOverlay show={accountProcessing} />
    </>
  );
};

ModalExtraData.propTypes = {
  onClose: PropTypes.func,
};

export default ModalExtraData;
